if (!customElements.get('size-guide-modal')) {
class SizeGuideModal extends HTMLElement {
    constructor() {
        super();
        this.currentUnit = 'in';
        this.currentRegion = 'us';
        this.currentProductType = 'bras';
        this.availableSizes = null;
        
        // Size data loaded from JSON files
        this.sizeData = {
            braCupBand: null,
            body: null,
            bralette: null,
            braFull: null,
            international: null,
            legGarters: null,
            braToThekiss: null
        };
        this.dataLoaded = false;
    }

    connectedCallback() {
        this.parseAvailableSizes();
        this.setupEventListeners();
        this.loadSizeData().then(() => {
            this.initializeCalculatorDropdowns();
        });

        // Auto-select tab based on product type data attribute (set by Liquid)
        const autoTab = this.dataset.autoTab;
        if (autoTab) {
            this.switchProductTab(autoTab);
        }

        // URL ?tab= param overrides data-auto-tab
        const urlTab = new URLSearchParams(window.location.search).get('tab');
        if (urlTab) {
            const tabMap = {
                '1': 'bras', '2': 'bralettes', '3': 'panties', '4': 'bodysuits', '5': 'leggarters',
                'bras': 'bras', 'bralettes': 'bralettes', 'panties': 'panties', 'bodysuits': 'bodysuits', 'leggarters': 'leggarters'
            };
            const tabType = tabMap[urlTab];
            if (tabType) this.switchProductTab(tabType);
        }
    }

    /**
     * Load size chart data from JSON files
     * URLs are passed via data attributes from Liquid template
     */
    async loadSizeData() {
        const urls = {
            braCupBand: this.dataset.braCupBandUrl,
            body: this.dataset.bodyUrl,
            bralette: this.dataset.braletteUrl,
            braFull: this.dataset.braFullUrl,
            international: this.dataset.internationalUrl,
            legGarters: this.dataset.legGartersUrl,
            braToThekiss: this.dataset.braToThekissUrl
        };

        const fetchPromises = Object.entries(urls).map(async ([key, url]) => {
            if (!url) {
                console.warn(`[SizeGuide] No URL provided for ${key}`);
                return;
            }
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                this.sizeData[key] = await response.json();
                console.log(`[SizeGuide] Loaded ${key} data`);
            } catch (e) {
                console.error(`[SizeGuide] Failed to load ${key}:`, e);
            }
        });

        await Promise.all(fetchPromises);
        this.dataLoaded = true;
        console.log('[SizeGuide] All size data loaded');
    }

    parseAvailableSizes() {
        try {
            const sizesData = this.dataset.availableSizes;
            if (sizesData) {
                this.availableSizes = JSON.parse(sizesData);
                console.log('[SizeGuide] Available sizes loaded:', this.availableSizes);
            } else {
                console.log('[SizeGuide] No available sizes data found on element');
            }
        } catch (e) {
            console.warn('[SizeGuide] Could not parse available sizes:', e);
            this.availableSizes = null;
        }
    }

    /**
     * Check if a bra size (band + cup) is available for this product
     * @param {string} band - e.g., "32"
     * @param {string} cup - e.g., "A", "DD"
     * @returns {boolean}
     */
    isBraSizeAvailable(band, cup) {
        console.log('[SizeGuide] Checking bra size availability:', { band, cup, availableSizes: this.availableSizes });
        
        if (!this.availableSizes) {
            console.log('[SizeGuide] No available sizes data, assuming available');
            return true;
        }
        
        const { bands, cups, variants } = this.availableSizes;
        
        // If product doesn't have Band/Cup options, skip validation
        if (!bands || bands.length === 0 || !cups || cups.length === 0) {
            console.log('[SizeGuide] No bands or cups in data, skipping validation');
            return true;
        }
        
        // Check if band exists
        const bandStr = String(band);
        const bandExists = bands.some(b => String(b).trim() === bandStr);
        console.log('[SizeGuide] Band check:', { bandStr, bands, bandExists });
        if (!bandExists) {
            console.log('[SizeGuide] Band not found in available bands');
            return false;
        }
        
        // Normalize cup for comparison
        const cupNorm = String(cup).trim().toUpperCase();
        const cupExists = cups.some(c => {
            const cNorm = String(c).trim().toUpperCase();
            // Exact match
            if (cNorm === cupNorm) return true;
            // Check if it's a partial match (e.g., "DD" matches "DD / E")
            if (cupNorm.includes('/')) {
                const cupParts = cupNorm.split(/\s*\/\s*/);
                return cupParts.some(part => cNorm === part);
            }
            if (cNorm.includes('/')) {
                const cParts = cNorm.split(/\s*\/\s*/);
                return cParts.some(part => part === cupNorm);
            }
            return false;
        });
        console.log('[SizeGuide] Cup check:', { cupNorm, cups, cupExists });
        if (!cupExists) {
            console.log('[SizeGuide] Cup not found in available cups');
            return false;
        }
        
        // Check if the specific combination exists and is available in variants
        if (variants && variants.length > 0) {
            const matchingVariant = variants.find(v => {
                const title = String(v.title).toUpperCase();
                const hasBand = title.includes(bandStr);
                const hasCup = cupNorm.split(/\s*\/\s*/).some(part => title.includes(part));
                return hasBand && hasCup;
            });
            
            console.log('[SizeGuide] Variant check:', { matchingVariant });
            
            if (matchingVariant) {
                console.log('[SizeGuide] Found matching variant, available:', matchingVariant.available);
                return matchingVariant.available !== false;
            }
            
            console.log('[SizeGuide] No matching variant found - size not available');
            return false;
        }
        
        console.log('[SizeGuide] No variants to check, assuming available based on band/cup');
        return true;
    }

    /**
     * Check if a body size (XS, S, M, etc.) is available
     * @param {string} size - e.g., "S", "M", "XL"
     * @returns {boolean}
     */
    isBodySizeAvailable(size) {
        if (!this.availableSizes) return true;
        
        const { sizes, variants } = this.availableSizes;
        
        // If product doesn't have Size option, skip validation
        if (!sizes || sizes.length === 0) {
            return true;
        }
        
        const sizeNorm = String(size).trim().toUpperCase();
        const sizeExists = sizes.some(s => String(s).trim().toUpperCase() === sizeNorm);
        
        if (!sizeExists) return false;
        
        // Check variant availability
        if (variants && variants.length > 0) {
            const matchingVariant = variants.find(v => {
                const title = String(v.title).trim().toUpperCase();
                return title === sizeNorm || title.includes(sizeNorm);
            });
            
            if (matchingVariant) {
                return matchingVariant.available !== false;
            }
        }
        
        return true;
    }

    setupEventListeners() {
        // Close button
        const closeBtn = this.querySelector('.close-btn');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.close());
        }

        // Overlay click
        const overlay = document.getElementById('sizeGuideOverlay');
        if (overlay) {
            overlay.addEventListener('click', () => this.close());
        }

        // ESC key to close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.classList.contains('active')) {
                this.close();
            }
        });

        // Product tabs
        this.querySelectorAll('.sg-product-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                const productType = tab.dataset.productType;
                this.switchProductTab(productType);
            });
        });

        // Global unit toggle (in header)
        this.querySelectorAll('.sg-header .unit-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.switchGlobalUnit(btn.dataset.unit);
            });
        });

        // Calculator unit toggles — sync ALL toggles globally
        this.querySelectorAll('.unit-toggle-calc .unit-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.switchGlobalUnit(btn.dataset.unit);
            });
        });

        // Section unit toggles — sync ALL toggles globally
        this.querySelectorAll('.unit-toggle-section .unit-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.switchGlobalUnit(btn.dataset.unit);
            });
        });

        // Region selects
        this.querySelectorAll('.sg-region-select').forEach(select => {
            select.addEventListener('change', () => {
                const tableFor = select.dataset.regionFor;
                this.switchRegion(select.value, tableFor);
            });
        });

        // Calculator buttons
        this.querySelectorAll('.sg-calc-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.calculateSize(btn);
            });
        });

        // Calculator select changes
        this.querySelectorAll('.sg-calc-select').forEach(select => {
            select.addEventListener('change', () => {
                this.validateCalculatorInputs(select);
            });
        });

        // Recalculate buttons
        this.querySelectorAll('[data-action="recalculate"]').forEach(btn => {
            btn.addEventListener('click', () => {
                this.resetCalculator(btn);
            });
        });

        // Help links - show measure guide
        this.querySelectorAll('[data-action="show-measure-guide"]').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                this.scrollToMeasureGuide();
            });
        });

        // Explore Full Chart button
        const fullChartBtn = this.querySelector('#sg-explore-full-chart');
        if (fullChartBtn) {
            fullChartBtn.addEventListener('click', () => {
                this.toggleFullChart();
            });
        }

        // Full chart unit toggles
        this.querySelectorAll('.sg-full-chart-table-section .unit-toggle-section .unit-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.switchGlobalUnit(btn.dataset.unit);
            });
        });

        // Add to cart from result
        this.querySelectorAll('.sg-result-add-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.addToCart();
            });
        });
    }

    // ===== PRODUCT TABS =====
    switchProductTab(productType) {
        this.currentProductType = productType;

        // Update tab states
        this.querySelectorAll('.sg-product-tab').forEach(tab => {
            tab.classList.toggle('active', tab.dataset.productType === productType);
        });

        // Update content visibility
        this.querySelectorAll('.sg-product-content').forEach(content => {
            content.classList.toggle('active', content.dataset.content === productType);
        });

        // Scroll active tab into view if needed
        const activeTab = this.querySelector(`.sg-product-tab[data-product-type="${productType}"]`);
        if (activeTab) {
            activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }

    // ===== UNIT SWITCHING =====
    switchGlobalUnit(unit) {
        this.currentUnit = unit;

        // Update all unit toggle buttons
        this.querySelectorAll('.unit-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.unit === unit);
        });

        // Update all tables
        this.updateAllTableUnits(unit);

        // Update ALL calculator dropdowns (every tab, not just active)
        this.querySelectorAll('.sg-product-content').forEach(content => {
            this.updateCalculatorDropdownUnits(unit, content);
        });
    }

    switchCalcUnit(unit, calcSection) {
        // Update buttons in this calc section
        calcSection.querySelectorAll('.unit-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.unit === unit);
        });

        // Update related dropdowns
        const productContent = calcSection.closest('.sg-product-content');
        if (productContent) {
            this.updateCalculatorDropdownUnits(unit, productContent);
        }
    }

    switchSectionUnit(unit, tableTarget, toggleElement) {
        // Update buttons in this toggle
        toggleElement.querySelectorAll('.unit-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.unit === unit);
        });

        // Update the specific table
        const table = this.querySelector(`[data-table-container="${tableTarget}"]`);
        if (table) {
            this.updateTableUnit(table, unit);
        }
    }

    updateAllTableUnits(unit) {
        this.querySelectorAll('.sg-table').forEach(table => {
            this.updateTableUnit(table, unit);
        });
    }

    updateTableUnit(table, unit) {
        table.querySelectorAll('td[data-in], td[data-cm]').forEach(cell => {
            const value = cell.dataset[unit] || cell.dataset['in'];
            if (value) {
                if (unit === 'cm') {
                    cell.textContent = value.includes('-') ? value : value + ' cm';
                } else {
                    cell.textContent = value;
                }
            }
        });
    }

    // ===== REGION SWITCHING =====
    switchRegion(region, tableFor) {
        this.currentRegion = region;

        const table = this.querySelector(`table[data-table-container="${tableFor}"], [data-table-container="${tableFor}"] table`);
        if (table) {
            table.querySelectorAll('td[data-us], td[data-uk], td[data-eu]').forEach(cell => {
                const value = cell.dataset[region];
                if (value) {
                    cell.textContent = value;
                }
            });
        }
    }

    // ===== CALCULATOR =====
    initializeCalculatorDropdowns() {
        // Bra calculator
        this.populateSelect('calc-bra-underbust', this.getUnderbustOptions(this.currentUnit));
        this.populateSelect('calc-bra-bust', this.getBustMeasurementOptions(this.currentUnit));

        // Panties calculator
        this.populateSelect('calc-panties-waist', this.getWaistOptions(this.currentUnit));
        this.populateSelect('calc-panties-hips', this.getHipsOptions(this.currentUnit));

        // Bodysuits calculator (was swimwear)
        this.populateSelect('calc-bodysuit-bust', this.getBustOptions(this.currentUnit));
        this.populateSelect('calc-bodysuit-waist', this.getWaistOptions(this.currentUnit));
        this.populateSelect('calc-bodysuit-hips', this.getHipsOptions(this.currentUnit));

        // Bralettes calculator
        this.populateSelect('calc-bralette-underbust', this.getBraletteUnderbustOptions(this.currentUnit));
        this.populateSelect('calc-bralette-bust', this.getBustOptions(this.currentUnit));

        // Leg garters calculator
        this.populateSelect('calc-leggarter-thigh', this.getThighOptions(this.currentUnit));
    }

    populateSelect(selectId, options) {
        const select = this.querySelector(`#${selectId}`);
        if (!select) return;

        // Keep the first option (placeholder)
        const placeholder = select.querySelector('option');
        select.innerHTML = '';
        if (placeholder) select.appendChild(placeholder);

        options.forEach(opt => {
            const option = document.createElement('option');
            option.value = opt.value;
            option.textContent = opt.label;
            select.appendChild(option);
        });
    }

    // Task 1: Underbust (bra) — 26.5–36.5" step 0.5 / 68–92 cm step 1
    getUnderbustOptions(unit = 'in') {
        const options = [];
        if (unit === 'in') {
            for (let i = 53; i <= 73; i++) {
                const x = i / 2;
                options.push({ value: String(x), label: `${x}"` });
            }
        } else {
            for (let i = 68; i <= 92; i++) {
                options.push({ value: i.toString(), label: `${i} cm` });
            }
        }
        return options;
    }

    // Task 1: Bust (bra) — 32–43.5" step 0.5 / 82–110 cm step 1
    getBustMeasurementOptions(unit = 'in') {
        const options = [];
        if (unit === 'in') {
            for (let i = 64; i <= 87; i++) {
                const x = i / 2;
                options.push({ value: String(x), label: `${x}"` });
            }
        } else {
            for (let i = 82; i <= 110; i++) {
                options.push({ value: i.toString(), label: `${i} cm` });
            }
        }
        return options;
    }

    // Task 1: Waist — 24–38.5" step 0.5 / 61–97 cm step 1
    getWaistOptions(unit = 'in') {
        const options = [];
        if (unit === 'in') {
            for (let i = 48; i <= 77; i++) {
                const x = i / 2;
                options.push({ value: String(x), label: `${x}"` });
            }
        } else {
            for (let i = 61; i <= 97; i++) {
                options.push({ value: i.toString(), label: `${i} cm` });
            }
        }
        return options;
    }

    // Task 1: Hips — 35.5–48" step 0.5 / 90–122 cm step 1
    getHipsOptions(unit = 'in') {
        const options = [];
        if (unit === 'in') {
            for (let i = 71; i <= 96; i++) {
                const x = i / 2;
                options.push({ value: String(x), label: `${x}"` });
            }
        } else {
            for (let i = 90; i <= 122; i++) {
                options.push({ value: i.toString(), label: `${i} cm` });
            }
        }
        return options;
    }

    // Task 1: Bust (all others — panties, bodysuits, bralettes) — 32–46" step 0.5 / 82–117 cm step 1
    getBustOptions(unit = 'in') {
        const options = [];
        if (unit === 'in') {
            for (let i = 64; i <= 92; i++) {
                const x = i / 2;
                options.push({ value: String(x), label: `${x}"` });
            }
        } else {
            for (let i = 82; i <= 117; i++) {
                options.push({ value: i.toString(), label: `${i} cm` });
            }
        }
        return options;
    }

    // Task 6: Bralette underbust — 24.5–40" step 0.5 / 63–102 cm step 1
    getBraletteUnderbustOptions(unit = 'in') {
        const options = [];
        if (unit === 'in') {
            for (let i = 49; i <= 80; i++) {
                const x = i / 2;
                options.push({ value: String(x), label: `${x}"` });
            }
        } else {
            for (let i = 63; i <= 102; i++) {
                options.push({ value: i.toString(), label: `${i} cm` });
            }
        }
        return options;
    }

    // Task 8: Thigh — 17.5–33.5" step 0.5 / 45–85 cm step 1
    getThighOptions(unit = 'in') {
        const options = [];
        if (unit === 'in') {
            for (let i = 35; i <= 67; i++) {
                const x = i / 2;
                options.push({ value: String(x), label: `${x}"` });
            }
        } else {
            for (let i = 45; i <= 85; i++) {
                options.push({ value: i.toString(), label: `${i} cm` });
            }
        }
        return options;
    }

    updateCalculatorDropdownUnits(unit, container = this) {
        const productContent = container.classList && container.classList.contains('sg-product-content')
            ? container
            : container.querySelector('.sg-product-content.active');

        if (!productContent) return;

        const productType = productContent.dataset.content;

        // Save current values before updating
        const savedValues = {};
        productContent.querySelectorAll('.sg-calc-select').forEach(select => {
            savedValues[select.id] = select.value;
        });

        // Update dropdowns based on product type
        if (productType === 'bras') {
            this.populateSelect('calc-bra-underbust', this.getUnderbustOptions(unit));
            this.populateSelect('calc-bra-bust', this.getBustMeasurementOptions(unit));
        } else if (productType === 'panties') {
            this.populateSelect('calc-panties-waist', this.getWaistOptions(unit));
            this.populateSelect('calc-panties-hips', this.getHipsOptions(unit));
        } else if (productType === 'bodysuits') {
            this.populateSelect('calc-bodysuit-bust', this.getBustOptions(unit));
            this.populateSelect('calc-bodysuit-waist', this.getWaistOptions(unit));
            this.populateSelect('calc-bodysuit-hips', this.getHipsOptions(unit));
        } else if (productType === 'bralettes') {
            this.populateSelect('calc-bralette-underbust', this.getBraletteUnderbustOptions(unit));
            this.populateSelect('calc-bralette-bust', this.getBustOptions(unit));
        } else if (productType === 'leggarters') {
            this.populateSelect('calc-leggarter-thigh', this.getThighOptions(unit));
        }

        // Restore values if they still exist
        Object.keys(savedValues).forEach(selectId => {
            const select = this.querySelector(`#${selectId}`);
            if (select) {
                const option = select.querySelector(`option[value="${savedValues[selectId]}"]`);
                if (option) {
                    select.value = savedValues[selectId];
                } else {
                    select.value = '';
                }
            }
        });

        // Revalidate button state
        const firstSelect = productContent.querySelector('.sg-calc-select');
        if (firstSelect) {
            this.validateCalculatorInputs(firstSelect);
        }
    }

    validateCalculatorInputs(changedSelect) {
        const calcSection = changedSelect.closest('.sg-calculator');
        const findBtn = calcSection.querySelector('.sg-calc-btn');

        if (!findBtn) return;

        const selects = calcSection.querySelectorAll('.sg-calc-select');
        const allFilled = Array.from(selects).every(select => select.value !== '');

        findBtn.disabled = !allFilled;
    }

    calculateSize(btn) {
        const productContent = btn.closest('.sg-product-content');
        const productType = productContent.dataset.content;

        let resultSize = '';
        let resultRegions = '';
        let isAvailable = true;

        if (productType === 'bras') {
            const calcSection = productContent.querySelector('.sg-calculator');
            const unit = this.getCalcUnit(calcSection);
            const underbustRaw = this.querySelector('#calc-bra-underbust').value;
            const bustRaw = this.querySelector('#calc-bra-bust').value;

            if (underbustRaw && bustRaw) {
                const underbust = parseFloat(underbustRaw);
                const bust = parseFloat(bustRaw);
                
                // Validate: bust must be > underbust
                if (bust <= underbust) {
                    resultSize = '—';
                    resultRegions = 'Bust must be larger than underbust. Please re-check your measurements.';
                    this.showResult('bra-size-result', 'bra-result-size', 'bra-result-regions', resultSize, resultRegions, true);
                    return;
                }

                // Use the full chart lookup approach for accurate results
                const fullChartResult = this.calculateBraSizeFromFullChart(underbust, bust, unit);
                
                if (fullChartResult && fullChartResult.noMatch) {
                    // Bust is outside the valid range for this band — show "no match" screen
                    this.showNoMatchResult('bra-size-result');
                    return;
                } else if (fullChartResult && fullChartResult.size) {
                    resultSize = fullChartResult.size;
                    const bandNum = fullChartResult.band;
                    const cupUS = fullChartResult.cup;
                    const bandEU = this.bandToEU(bandNum);
                    const cupMapping = this.getCupRegionMapping(cupUS);
                    resultRegions = `US ${bandNum}${cupUS} • UK ${bandNum}${cupMapping.uk} • EU ${bandEU}${cupMapping.eu}`;
                    isAvailable = this.isBraSizeAvailable(bandNum, cupUS);
                } else {
                    // Fallback: use independent band+cup calculation with validation
                    const bandResult = this.underbustToBand(underbust, unit);
                    const band = bandResult.us;
                    const bandEU = bandResult.eu;
                    const diff = bust - underbust;
                    const cupResult = this.bustDiffToCup(diff, unit);

                    if (band && cupResult) {
                        // Validate against full chart
                        const validated = this.validateBraSizeAgainstChart(band, cupResult.us, bust, unit);
                        resultSize = `${validated.band}${validated.cup}`;
                        const valBandEU = this.bandToEU(validated.band);
                        const valCupMapping = this.getCupRegionMapping(validated.cup);
                        resultRegions = `US ${validated.band}${validated.cup} • UK ${validated.band}${valCupMapping.uk} • EU ${valBandEU}${valCupMapping.eu}`;
                        isAvailable = this.isBraSizeAvailable(validated.band, validated.cup);
                    }
                }
            }

            this.showResult('bra-size-result', 'bra-result-size', 'bra-result-regions', resultSize, resultRegions, isAvailable);
        }
        else if (productType === 'panties') {
            const calcSection = productContent.querySelector('.sg-calculator');
            const unit = this.getCalcUnit(calcSection);
            const waistRaw = parseFloat(this.querySelector('#calc-panties-waist').value);
            const hipsRaw = parseFloat(this.querySelector('#calc-panties-hips').value);
            
            // Convert to inches for the panties calculator (uses inch-based ranges)
            const waistIn = unit === 'cm' ? waistRaw / 2.54 : waistRaw;
            const hipsIn = unit === 'cm' ? hipsRaw / 2.54 : hipsRaw;

            const pantiesResult = this.calculatePantiesSize(waistIn, hipsIn);

            if (pantiesResult && pantiesResult.noMatch) {
                // Waist vs hips differ by more than 1 size → no match
                this.showNoMatchResult('panties-size-result');
                return;
            }

            resultSize = pantiesResult ? pantiesResult.size : 'M';
            resultRegions = this.getBodySizeRegions(resultSize);
            
            isAvailable = this.isBodySizeAvailable(resultSize);

            this.showResult('panties-size-result', 'panties-result-size', 'panties-result-regions', resultSize, resultRegions, isAvailable);
        }
        else if (productType === 'bodysuits') {
            // TODO (next sprint): Bodysuit sizing logic
            // Priority: bust > hips > waist
            // Max difference between parameters: 1 size
            // Will implement cross-parameter validation
            const calcSection = productContent.querySelector('.sg-calculator');
            const unit = this.getCalcUnit(calcSection);
            const bustRaw = parseFloat(this.querySelector('#calc-bodysuit-bust').value);
            const waistRaw = parseFloat(this.querySelector('#calc-bodysuit-waist').value);
            const hipsRaw = parseFloat(this.querySelector('#calc-bodysuit-hips').value);
            
            const bustCm = unit === 'in' ? Math.round(bustRaw * 2.54) : bustRaw;
            const waistCm = unit === 'in' ? Math.round(waistRaw * 2.54) : waistRaw;
            const hipsCm = unit === 'in' ? Math.round(hipsRaw * 2.54) : hipsRaw;

            const bustSize = this.getBustSize(bustCm);
            const bodySize = this.calculateBodySize(waistCm, hipsCm);
            
            const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];
            const bustIndex = sizeOrder.indexOf(bustSize);
            const bodyIndex = sizeOrder.indexOf(bodySize);
            resultSize = bustIndex > bodyIndex ? bustSize : bodySize;
            
            resultRegions = this.getBodySizeRegions(resultSize);
            
            isAvailable = this.isBodySizeAvailable(resultSize);

            this.showResult('bodysuit-size-result', 'bodysuit-result-size', 'bodysuit-result-regions', resultSize, resultRegions, isAvailable);
        }
        else if (productType === 'bralettes') {
            const calcSection = productContent.querySelector('.sg-calculator');
            const unit = this.getCalcUnit(calcSection);
            const underbustRaw = parseFloat(this.querySelector('#calc-bralette-underbust').value);
            const bustRaw = parseFloat(this.querySelector('#calc-bralette-bust').value);

            // Convert to cm for lookup
            const underbustCm = unit === 'in' ? Math.round(underbustRaw * 2.54) : underbustRaw;
            const bustCm = unit === 'in' ? Math.round(bustRaw * 2.54) : bustRaw;

            resultSize = this.calculateBraletteSize(underbustCm, bustCm);
            resultRegions = this.getBodySizeRegions(resultSize);

            isAvailable = this.isBodySizeAvailable(resultSize);

            this.showResult('bralette-size-result', 'bralette-result-size', 'bralette-result-regions', resultSize, resultRegions, isAvailable);
        }
        else if (productType === 'leggarters') {
            const calcSection = productContent.querySelector('.sg-calculator');
            const unit = this.getCalcUnit(calcSection);
            const thighRaw = parseFloat(this.querySelector('#calc-leggarter-thigh').value);

            // Convert to cm for lookup
            const thighCm = unit === 'in' ? Math.round(thighRaw * 2.54) : thighRaw;

            resultSize = this.calculateLegGarterSize(thighCm);
            resultRegions = ''; // No international conversions for leg garters

            this.showResult('leggarter-size-result', 'leggarter-result-size', 'leggarter-result-regions', resultSize, resultRegions, true);
        }
    }

    getCalcUnit(calcSection) {
        const activeBtn = calcSection.querySelector('.unit-toggle-calc .unit-btn.active');
        return activeBtn ? activeBtn.dataset.unit : this.currentUnit;
    }

    toInches(value, unit) {
        return unit === 'cm' ? value / 2.54 : value;
    }

    /**
     * Convert underbust measurement to band size using JSON data
     * Edge values mapped to nearest band size
     */
    underbustToBand(underbust, unit = 'in') {
        const data = this.sizeData.braCupBand;
        
        // Fallback to hardcoded logic if JSON not loaded
        if (!data || !data.bandSize) {
            const underbustIn = unit === 'cm' ? underbust / 2.54 : underbust;
            // Map edge values to nearest band
            if (underbustIn <= 28.3) return { us: '32', uk: '32', eu: '70' };
            if (underbustIn <= 30.3) return { us: '34', uk: '34', eu: '75' };
            if (underbustIn <= 32.3) return { us: '36', uk: '36', eu: '80' };
            if (underbustIn <= 34.3) return { us: '38', uk: '38', eu: '85' };
            return { us: '40', uk: '40', eu: '90' };
        }

        const bandSizes = unit === 'cm' ? data.bandSize.cm : data.bandSize.inches;
        
        // Find the matching band size range
        for (const entry of bandSizes) {
            if (underbust >= entry.min && underbust <= entry.max) {
                return { us: entry.us, uk: entry.uk, eu: entry.eu };
            }
        }
        
        // If below minimum, return smallest (edge value mapping)
        if (underbust < bandSizes[0].min) {
            const first = bandSizes[0];
            return { us: first.us, uk: first.uk, eu: first.eu };
        }
        
        // If between bands, find nearest
        for (let i = 0; i < bandSizes.length - 1; i++) {
            if (underbust > bandSizes[i].max && underbust < bandSizes[i + 1].min) {
                // Map to the next band (round up)
                const next = bandSizes[i + 1];
                return { us: next.us, uk: next.uk, eu: next.eu };
            }
        }
        
        // If above maximum, return largest
        const last = bandSizes[bandSizes.length - 1];
        return { us: last.us, uk: last.uk, eu: last.eu };
    }

    /**
     * Convert bust-underbust difference to cup size using JSON data
     * Uses DD and DDD (no slashes)
     */
    bustDiffToCup(diff, unit = 'in') {
        const data = this.sizeData.braCupBand;
        
        // Fallback to hardcoded logic if JSON not loaded
        if (!data || !data.cupSize) {
            const diffIn = unit === 'cm' ? diff / 2.54 : diff;
            // Boundary values (e.g. 5.5) go to the smaller cup (A, not B)
            const cups = [
                { min: 0, max: 4.7, us: 'AA', uk: 'AA', eu: 'AA' },
                { min: 4.7, max: 5.5, us: 'A', uk: 'A', eu: 'A' },
                { min: 5.5, max: 6.3, us: 'B', uk: 'B', eu: 'B' },
                { min: 6.3, max: 7.1, us: 'C', uk: 'C', eu: 'C' },
                { min: 7.1, max: 7.9, us: 'D', uk: 'D', eu: 'D' },
                { min: 7.9, max: 8.7, us: 'DD', uk: 'DD', eu: 'E' },
                { min: 8.7, max: 9.4, us: 'DDD', uk: 'E', eu: 'F' },
                { min: 9.4, max: 99, us: 'G', uk: 'F', eu: 'G' }
            ];
            for (const cup of cups) {
                if (diffIn >= cup.min && diffIn <= cup.max) {
                    return { us: cup.us, uk: cup.uk, eu: cup.eu };
                }
            }
            return cups[cups.length - 1];
        }

        const cupSizes = unit === 'cm' ? data.cupSize.cm : data.cupSize.inches;
        
        // Find the matching cup size range (boundary values go to smaller cup)
        for (const entry of cupSizes) {
            if (diff >= entry.min && diff <= entry.max) {
                return { us: entry.us, uk: entry.uk, eu: entry.eu };
            }
        }
        
        // If below minimum, return smallest
        if (diff < cupSizes[0].min) {
            const first = cupSizes[0];
            return { us: first.us, uk: first.uk, eu: first.eu };
        }
        
        // If above maximum, return largest
        const last = cupSizes[cupSizes.length - 1];
        return { us: last.us, uk: last.uk, eu: last.eu };
    }

    /**
     * Get cup region mapping (US→UK, US→EU) without slashes
     */
    getCupRegionMapping(cupUS) {
        const map = {
            'AA':  { uk: 'AA', eu: 'AA' },
            'A':   { uk: 'A',  eu: 'A' },
            'B':   { uk: 'B',  eu: 'B' },
            'C':   { uk: 'C',  eu: 'C' },
            'D':   { uk: 'D',  eu: 'D' },
            'DD':  { uk: 'DD', eu: 'E' },
            'DDD': { uk: 'E',  eu: 'F' },
            'G':   { uk: 'F',  eu: 'G' }
        };
        return map[cupUS] || { uk: cupUS, eu: cupUS };
    }

    /**
     * Calculate bra size using the FULL SIZE CHART directly.
     * 1. Given underbust → find band
     * 2. Given bust → find what cup sizes exist for that band at that bust
     * 3. Return the correct band+cup combo
     * This avoids the bug where independent band+cup calculation produces non-existent sizes.
     *
     * Returns null (no match) when bust is outside the valid range for the band.
     */
    calculateBraSizeFromFullChart(underbust, bust, unit = 'in') {
        const fullData = this.sizeData.braFull;
        if (!fullData) return null;

        const unitData = unit === 'cm' ? fullData.cm : fullData.inches;
        if (!unitData || !unitData.cupsByBand) return null;

        // Step 1: Find the band from underbust
        const bandResult = this.underbustToBand(underbust, unit);
        const band = bandResult.us;

        // Step 2: Look up what cups exist for this band, and find which cup range the bust falls into
        const bandCups = unitData.cupsByBand[band];
        if (!bandCups) return null;

        const cupOrder = ['AA', 'A', 'B', 'C', 'D', 'DD', 'DDD', 'G'];

        // Determine valid range for this band (first available cup min → last available cup max)
        const availableCups = cupOrder.filter(c => bandCups[c] !== null && bandCups[c] !== undefined);
        if (availableCups.length === 0) return null;

        const firstCup = availableCups[0];
        const lastCup = availableCups[availableCups.length - 1];
        const bandBustMin = bandCups[firstCup].bustMin;
        const bandBustMax = bandCups[lastCup].bustMax;

        // If bust is outside the valid range for this band → no match
        if (bust < bandBustMin || bust > bandBustMax) {
            return { noMatch: true, band: band };
        }

        // Find exact cup match
        for (const cup of cupOrder) {
            const cupData = bandCups[cup];
            if (!cupData) continue; // This cup doesn't exist for this band

            if (bust >= cupData.bustMin && bust <= cupData.bustMax) {
                return { size: `${band}${cup}`, band: band, cup: cup };
            }
        }

        // Bust is within the band's overall range but falls in a gap between cups — find closest
        let bestMatch = null;
        let closestDistance = Infinity;
        for (const cup of availableCups) {
            const cupData = bandCups[cup];
            const midBust = (cupData.bustMin + cupData.bustMax) / 2;
            const dist = Math.abs(bust - midBust);
            if (dist < closestDistance) {
                closestDistance = dist;
                bestMatch = { size: `${band}${cup}`, band: band, cup: cup };
            }
        }

        return bestMatch;
    }

    /**
     * Validate a band+cup combination against the full chart.
     * If the combo doesn't exist, find the closest valid size.
     */
    validateBraSizeAgainstChart(band, cup, bust, unit = 'in') {
        const fullData = this.sizeData.braFull;
        if (!fullData) return { band, cup };

        const unitData = unit === 'cm' ? fullData.cm : fullData.inches;
        if (!unitData || !unitData.cupsByBand) return { band, cup };

        const bandCups = unitData.cupsByBand[band];
        if (!bandCups) return { band, cup };

        // Check if this exact combo exists
        if (bandCups[cup] !== null && bandCups[cup] !== undefined) {
            return { band, cup };
        }

        // Combo doesn't exist — find the closest valid cup for this band
        const cupOrder = ['AA', 'A', 'B', 'C', 'D', 'DD', 'DDD', 'G'];
        const targetIdx = cupOrder.indexOf(cup);
        
        // Search downward first (smaller cup), then upward
        for (let offset = 1; offset < cupOrder.length; offset++) {
            // Try smaller cup first
            const downIdx = targetIdx - offset;
            if (downIdx >= 0 && bandCups[cupOrder[downIdx]] !== null && bandCups[cupOrder[downIdx]] !== undefined) {
                return { band, cup: cupOrder[downIdx] };
            }
            // Then try larger cup
            const upIdx = targetIdx + offset;
            if (upIdx < cupOrder.length && bandCups[cupOrder[upIdx]] !== null && bandCups[cupOrder[upIdx]] !== undefined) {
                return { band, cup: cupOrder[upIdx] };
            }
        }

        return { band, cup };
    }

    /**
     * Fallback band to EU conversion if JSON not available
     */
    bandToEUFallback(band) {
        const map = { '28': '60', '30': '65', '32': '70', '34': '75', '36': '80', '38': '85', '40': '90', '42': '95', '44': '100', '46': '105', '48': '110' };
        return map[String(band)] || band;
    }

    /**
     * Convert US band to EU size using JSON data
     */
    bandToEU(band) {
        const data = this.sizeData.braCupBand;
        
        if (!data || !data.bandSize) {
            return this.bandToEUFallback(band);
        }

        const bandEntry = data.bandSize.inches.find(entry => entry.us === String(band));
        if (bandEntry) {
            return bandEntry.eu;
        }
        
        return this.bandToEUFallback(band);
    }

    /**
     * Get size based on bust measurement (for bodysuits) using JSON data
     */
    getBustSize(bustCm) {
        const data = this.sizeData.body;
        
        if (!data || !data.sizes) {
            if (bustCm <= 85) return 'XS';
            if (bustCm <= 89) return 'S';
            if (bustCm <= 93) return 'M';
            if (bustCm <= 97) return 'L';
            if (bustCm <= 102) return 'XL';
            if (bustCm <= 107) return '2XL';
            if (bustCm <= 112) return '3XL';
            return '4XL';
        }

        for (const entry of data.sizes) {
            if (bustCm >= entry.bust.cmMin && bustCm <= entry.bust.cmMax) {
                return entry.size;
            }
        }

        if (bustCm < data.sizes[0].bust.cmMin) {
            return data.sizes[0].size;
        }

        return data.sizes[data.sizes.length - 1].size;
    }

    /**
     * Calculate body size for panties/bodysuits based on measurements
     * PRIORITY: Hips are the primary measurement
     */
    calculateBodySize(waistCm, hipsCm) {
        const data = this.sizeData.body;
        
        if (!data || !data.sizes) {
            const sizeChart = [
                { size: 'XS',  hipsMin: 90,  hipsMax: 93 },
                { size: 'S',   hipsMin: 94,  hipsMax: 97 },
                { size: 'M',   hipsMin: 98,  hipsMax: 101 },
                { size: 'L',   hipsMin: 102, hipsMax: 105 },
                { size: 'XL',  hipsMin: 106, hipsMax: 110 },
                { size: '2XL', hipsMin: 111, hipsMax: 115 },
                { size: '3XL', hipsMin: 113, hipsMax: 117 },
                { size: '4XL', hipsMin: 118, hipsMax: 122 }
            ];

            for (const entry of sizeChart) {
                if (hipsCm >= entry.hipsMin && hipsCm <= entry.hipsMax) {
                    return entry.size;
                }
            }

            if (hipsCm < 90) return 'XS';
            if (hipsCm > 122) return '4XL';
            return 'M';
        }

        for (const entry of data.sizes) {
            if (hipsCm >= entry.hips.cmMin && hipsCm <= entry.hips.cmMax) {
                return entry.size;
            }
        }

        if (hipsCm < data.sizes[0].hips.cmMin) {
            return data.sizes[0].size;
        }

        return data.sizes[data.sizes.length - 1].size;
    }

    /**
     * Calculate bralette size based on underbust and bust (in cm)
     * Uses bralette JSON data
     */
    calculateBraletteSize(underbustCm, bustCm) {
        const data = this.sizeData.bralette;

        if (!data || !data.sizes) {
            // Fallback hardcoded logic
            const chart = [
                { size: 'XS',  ubMin: 63,  ubMax: 67,  bMin: 82,  bMax: 85 },
                { size: 'S',   ubMin: 68,  ubMax: 72,  bMin: 86,  bMax: 89 },
                { size: 'M',   ubMin: 73,  ubMax: 77,  bMin: 90,  bMax: 93 },
                { size: 'L',   ubMin: 78,  ubMax: 82,  bMin: 94,  bMax: 97 },
                { size: 'XL',  ubMin: 83,  ubMax: 87,  bMin: 98,  bMax: 102 },
                { size: '2XL', ubMin: 88,  ubMax: 92,  bMin: 103, bMax: 107 },
                { size: '3XL', ubMin: 93,  ubMax: 97,  bMin: 108, bMax: 112 },
                { size: '4XL', ubMin: 98,  ubMax: 102, bMin: 113, bMax: 117 }
            ];

            // Try to match both measurements, fallback to bust if underbust doesn't match
            let bestMatch = null;
            for (const entry of chart) {
                const ubMatch = underbustCm >= entry.ubMin && underbustCm <= entry.ubMax;
                const bMatch = bustCm >= entry.bMin && bustCm <= entry.bMax;
                if (ubMatch && bMatch) return entry.size;
                if (bMatch && !bestMatch) bestMatch = entry.size;
            }
            if (bestMatch) return bestMatch;

            // If nothing matches, use bust primarily
            if (bustCm <= 85) return 'XS';
            if (bustCm <= 89) return 'S';
            if (bustCm <= 93) return 'M';
            if (bustCm <= 97) return 'L';
            if (bustCm <= 102) return 'XL';
            if (bustCm <= 107) return '2XL';
            if (bustCm <= 112) return '3XL';
            return '4XL';
        }

        // Use JSON data — try matching both, then pick the larger size for safe fit
        const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

        let ubSize = null;
        let bustSize = null;

        for (const entry of data.sizes) {
            if (underbustCm >= entry.underbust.cmMin && underbustCm <= entry.underbust.cmMax) {
                ubSize = entry.size;
            }
            if (bustCm >= entry.bust.cmMin && bustCm <= entry.bust.cmMax) {
                bustSize = entry.size;
            }
        }

        // If both match, return the larger one
        if (ubSize && bustSize) {
            const ubIdx = sizeOrder.indexOf(ubSize);
            const bIdx = sizeOrder.indexOf(bustSize);
            return ubIdx > bIdx ? ubSize : bustSize;
        }

        // If only one matches, use that
        if (bustSize) return bustSize;
        if (ubSize) return ubSize;

        // Fallback: closest bust match
        if (bustCm < data.sizes[0].bust.cmMin) return data.sizes[0].size;
        return data.sizes[data.sizes.length - 1].size;
    }

    /**
     * Calculate panties size using the "larger parameter wins" logic.
     * 1. Find size by waist alone
     * 2. Find size by hips alone
     * 3. Take the LARGER of the two sizes
     * 4. If difference > 1 size → no match
     *
     * @param {number} waistIn - waist in inches
     * @param {number} hipsIn - hips in inches
     * @returns {{ size: string } | { noMatch: true } | null}
     */
    calculatePantiesSize(waistIn, hipsIn) {
        const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

        // Waist ranges in inches (each size has a min–max)
        const waistRanges = [
            { size: 'XS',  min: 0,    max: 25.5 },
            { size: 'S',   min: 24,   max: 27 },
            { size: 'M',   min: 25.5, max: 28.5 },
            { size: 'L',   min: 27,   max: 30 },
            { size: 'XL',  min: 28.5, max: 32 },
            { size: '2XL', min: 30,   max: 34 },
            { size: '3XL', min: 32,   max: 36.5 },
            { size: '4XL', min: 34,   max: 38 }
        ];

        // Hips ranges in inches
        const hipsRanges = [
            { size: 'XS',  min: 0,    max: 36.5 },
            { size: 'S',   min: 35.5, max: 38.5 },
            { size: 'M',   min: 37,   max: 40 },
            { size: 'L',   min: 38.5, max: 41.5 },
            { size: 'XL',  min: 40,   max: 43.5 },
            { size: '2XL', min: 41.5, max: 45.5 },
            { size: '3XL', min: 43.5, max: 46 },
            { size: '4XL', min: 44.5, max: 48 }
        ];

        // Find best size for waist (pick the smallest size whose range covers the value)
        let waistSize = null;
        for (const range of waistRanges) {
            if (waistIn >= range.min && waistIn <= range.max) {
                waistSize = range.size;
                break;
            }
        }
        // Fallback: below minimum → XS, above maximum → 4XL
        if (!waistSize) {
            if (waistIn < waistRanges[0].min) waistSize = 'XS';
            else waistSize = '4XL';
        }

        // Find best size for hips
        let hipsSize = null;
        for (const range of hipsRanges) {
            if (hipsIn >= range.min && hipsIn <= range.max) {
                hipsSize = range.size;
                break;
            }
        }
        if (!hipsSize) {
            if (hipsIn < hipsRanges[0].min) hipsSize = 'XS';
            else hipsSize = '4XL';
        }

        const waistIdx = sizeOrder.indexOf(waistSize);
        const hipsIdx = sizeOrder.indexOf(hipsSize);

        // Max difference = 1 size
        if (Math.abs(waistIdx - hipsIdx) > 1) {
            return { noMatch: true };
        }

        // Take the larger of the two
        const finalSize = waistIdx >= hipsIdx ? waistSize : hipsSize;
        return { size: finalSize };
    }

    /**
     * Show the "No perfect match found" screen inside a result container.
     * Replaces result content with a light-background "no match" message.
     */
    showNoMatchResult(resultId) {
        const resultEl = this.querySelector(`#${resultId}`);
        if (!resultEl) return;

        const calcSection = resultEl.closest('.sg-product-content').querySelector('.sg-calculator');

        // Add the no-match class and make it active
        resultEl.classList.add('active', 'sg-result--no-match');
        resultEl.classList.remove('sg-result--unavailable');

        // Build or update no-match content
        let noMatchEl = resultEl.querySelector('.sg-no-match-content');
        if (!noMatchEl) {
            noMatchEl = document.createElement('div');
            noMatchEl.className = 'sg-no-match-content';
            noMatchEl.innerHTML = `
                <h3 class="sg-no-match-title">No perfect match found</h3>
                <div class="sg-no-match-dash">——</div>
                <p class="sg-no-match-text">Based on the measurements you entered, we don't have an exact size match.</p>
                <div class="sg-no-match-tip">
                    <span class="sg-no-match-tip-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="16" x2="12" y2="12"/>
                            <line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                    </span>
                    <span>Try adjusting your measurements slightly or explore nearby sizes.</span>
                </div>
                <button class="sg-no-match-recalc-btn" data-action="recalculate">Recalculate</button>
                <div class="sg-no-match-help">
                    <span class="sg-no-match-help-border"></span>
                    <span>Don't know how to measure?</span>
                    <a href="#" class="sg-no-match-help-link" data-action="show-measure-guide">See our guide</a>
                </div>
            `;
            resultEl.appendChild(noMatchEl);

            // Bind recalculate button
            const recalcBtn = noMatchEl.querySelector('[data-action="recalculate"]');
            if (recalcBtn) {
                recalcBtn.addEventListener('click', () => {
                    this.resetCalculator(recalcBtn);
                });
            }
            // Bind help link
            const helpLink = noMatchEl.querySelector('[data-action="show-measure-guide"]');
            if (helpLink) {
                helpLink.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.scrollToMeasureGuide();
                });
            }
        }

        // Hide the normal result elements, show no-match content
        resultEl.querySelectorAll('.sg-result-label, .sg-result-size, .sg-result-regions, .sg-result-tip, .sg-result-recalc-btn, .sg-result-unavailable').forEach(el => {
            el.style.display = 'none';
        });
        noMatchEl.style.display = 'block';

        // Hide calculator inputs
        if (calcSection) calcSection.classList.add('has-result');

        // Scroll to result
        resultEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /**
     * Calculate leg garter size based on thigh measurement (in cm)
     */
    calculateLegGarterSize(thighCm) {
        const data = this.sizeData.legGarters;

        if (!data || !data.sizes) {
            // Fallback hardcoded logic
            if (thighCm <= 59) return 'XS-M';
            if (thighCm <= 70) return 'L-XL';
            return '2XL-4XL';
        }

        for (const entry of data.sizes) {
            if (thighCm >= entry.thigh.cmMin && thighCm <= entry.thigh.cmMax) {
                return entry.size;
            }
        }

        if (thighCm < data.sizes[0].thigh.cmMin) return data.sizes[0].size;
        return data.sizes[data.sizes.length - 1].size;
    }

    /**
     * Get international size conversions using JSON data
     */
    getBodySizeRegions(size) {
        const data = this.sizeData.international;
        
        // Fallback with correct international data
        if (!data || !data.conversions) {
            const regions = {
                'XS':  'US 2 • UK 6 • EU 34',
                'S':   'US 4 • UK 8 • EU 36',
                'M':   'US 6 • UK 10 • EU 38',
                'L':   'US 8 • UK 12 • EU 40',
                'XL':  'US 10 • UK 14 • EU 42',
                '2XL': 'US 12 • UK 16 • EU 44',
                '3XL': 'US 14 • UK 18 • EU 46',
                '4XL': 'US 16 • UK 20 • EU 48'
            };
            return regions[size] || '';
        }

        const conversion = data.conversions.find(c => c.thekiss === size);
        if (!conversion) {
            return '';
        }

        return `US ${conversion.us} • UK ${conversion.uk} • EU ${conversion.eu}`;
    }

    showResult(resultId, sizeId, regionsId, size, regions, isAvailable = true) {
        const resultEl = this.querySelector(`#${resultId}`);
        const sizeEl = this.querySelector(`#${sizeId}`);
        const regionsEl = this.querySelector(`#${regionsId}`);
        const calcSection = resultEl ? resultEl.closest('.sg-product-content').querySelector('.sg-calculator') : null;

        if (sizeEl) sizeEl.textContent = size;
        if (regionsEl) regionsEl.textContent = regions;
        if (resultEl) resultEl.classList.add('active');

        // Handle size availability
        let unavailableMsg = resultEl ? resultEl.querySelector('.sg-result-unavailable') : null;
        
        if (!isAvailable) {
            // Add unavailable message if not exists
            if (!unavailableMsg) {
                unavailableMsg = document.createElement('div');
                unavailableMsg.className = 'sg-result-unavailable';
                unavailableMsg.innerHTML = `
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    <span>This size is not available for this product. Please choose a different size or browse our other styles.</span>
                `;
                if (regionsEl && regionsEl.parentNode) {
                    regionsEl.parentNode.insertBefore(unavailableMsg, regionsEl.nextSibling);
                }
            }
            unavailableMsg.style.display = 'flex';
            
            if (resultEl) resultEl.classList.add('sg-result--unavailable');
        } else {
            if (unavailableMsg) {
                unavailableMsg.style.display = 'none';
            }
            if (resultEl) resultEl.classList.remove('sg-result--unavailable');
        }

        // Hide calculator inputs when showing result
        if (calcSection) calcSection.classList.add('has-result');

        // Scroll to result
        if (resultEl) {
            resultEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    resetCalculator(btn) {
        const productContent = btn.closest('.sg-product-content');
        const calcSection = productContent.querySelector('.sg-calculator');

        // Hide result
        const result = productContent.querySelector('.sg-size-result');
        if (result) {
            result.classList.remove('active');
            result.classList.remove('sg-result--unavailable');
            result.classList.remove('sg-result--no-match');

            // Restore normal result elements hidden by showNoMatchResult
            result.querySelectorAll('.sg-result-label, .sg-result-size, .sg-result-regions, .sg-result-tip, .sg-result-recalc-btn').forEach(el => {
                el.style.display = '';
            });

            // Hide no-match content if present
            const noMatchContent = result.querySelector('.sg-no-match-content');
            if (noMatchContent) {
                noMatchContent.style.display = 'none';
            }
        }

        // Hide unavailable message
        const unavailableMsg = productContent.querySelector('.sg-result-unavailable');
        if (unavailableMsg) {
            unavailableMsg.style.display = 'none';
        }

        // Show calculator inputs again
        if (calcSection) calcSection.classList.remove('has-result');

        // Reset selects
        productContent.querySelectorAll('.sg-calc-select').forEach(select => {
            select.value = '';
        });

        // Disable find button
        const findBtn = productContent.querySelector('.sg-calc-btn');
        if (findBtn) findBtn.disabled = true;
    }

    scrollToMeasureGuide() {
        const activeContent = this.querySelector('.sg-product-content.active');
        if (activeContent) {
            const guide = activeContent.querySelector('.sg-measure-guide');
            if (guide) {
                guide.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }

    addToCart() {
        const activeContent = this.querySelector('.sg-product-content.active');
        const resultEl = activeContent?.querySelector('.sg-size-result.active');
        if (!resultEl) return;

        const productType = activeContent.dataset.content;
        const size = resultEl.querySelector('[id$="-result-size"]')?.textContent?.trim() || '';
        const isSet = this.dataset.isSet === 'true';

        this.close();

        if (size) {
            window.dispatchEvent(new CustomEvent('size-guide-apply-size', {
                detail: { productType, size, isSet }
            }));
        }
    }

    // ===== FULL CHART =====
    toggleFullChart() {
        const btn = this.querySelector('#sg-explore-full-chart');
        const content = this.querySelector('#sg-full-chart-content');
        if (!btn || !content) return;

        const isExpanded = btn.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
            // Collapse
            btn.setAttribute('aria-expanded', 'false');
            content.style.maxHeight = content.scrollHeight + 'px';
            requestAnimationFrame(() => {
                content.style.transition = 'max-height 0.4s ease, opacity 0.3s ease';
                content.style.maxHeight = '0';
                content.style.opacity = '0';
            });
            setTimeout(() => {
                content.style.display = 'none';
                content.style.transition = '';
                content.style.maxHeight = '';
                content.style.opacity = '';
            }, 400);
        } else {
            // Expand — build tables first if needed
            this.buildFullBraChart();
            this.buildBustLookupTable();

            btn.setAttribute('aria-expanded', 'true');
            content.style.display = 'block';
            content.style.maxHeight = '0';
            content.style.opacity = '0';
            requestAnimationFrame(() => {
                content.style.transition = 'max-height 0.5s ease, opacity 0.3s ease';
                content.style.maxHeight = content.scrollHeight + 'px';
                content.style.opacity = '1';
            });
            setTimeout(() => {
                content.style.maxHeight = 'none';
                content.style.transition = '';
            }, 500);
        }
    }

    buildFullBraChart() {
        const wrapper = this.querySelector('#sg-full-bra-chart-wrapper');
        if (!wrapper || wrapper.querySelector('table')) return; // Already built

        const fullData = this.sizeData.braFull;
        if (!fullData) {
            wrapper.innerHTML = '<p style="padding:16px;">Chart data not available.</p>';
            return;
        }

        const cups = ['AA', 'A', 'B', 'C', 'D', 'DD', 'DDD', 'G'];
        const bands = ['32', '34', '36', '38', '40'];

        const table = document.createElement('table');
        table.className = 'sg-table';
        table.setAttribute('data-table-container', 'full-bra-chart');

        // Header
        const thead = document.createElement('thead');
        const headerRow = document.createElement('tr');
        const bandTh = document.createElement('th');
        bandTh.textContent = 'Band';
        headerRow.appendChild(bandTh);
        const ubTh = document.createElement('th');
        ubTh.textContent = 'Underbust';
        headerRow.appendChild(ubTh);
        cups.forEach(cup => {
            const th = document.createElement('th');
            th.textContent = cup;
            headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        table.appendChild(thead);

        // Body
        const tbody = document.createElement('tbody');
        bands.forEach(band => {
            const row = document.createElement('tr');

            // Band cell
            const bandCell = document.createElement('td');
            bandCell.textContent = band;
            bandCell.style.fontWeight = '600';
            row.appendChild(bandCell);

            // Underbust cell
            const ubCell = document.createElement('td');
            const inBandData = fullData.inches.bands.find(b => b.band === band);
            const cmBandData = fullData.cm.bands.find(b => b.band === band);
            if (inBandData && cmBandData) {
                ubCell.setAttribute('data-in', inBandData.underbustRange);
                ubCell.setAttribute('data-cm', cmBandData.underbustRange);
                ubCell.textContent = this.currentUnit === 'cm' ? cmBandData.underbustRange : inBandData.underbustRange;
            }
            row.appendChild(ubCell);

            // Cup cells
            cups.forEach(cup => {
                const cell = document.createElement('td');
                const inCupData = fullData.inches.cupsByBand[band] ? fullData.inches.cupsByBand[band][cup] : null;
                const cmCupData = fullData.cm.cupsByBand[band] ? fullData.cm.cupsByBand[band][cup] : null;

                if (inCupData && cmCupData) {
                    cell.setAttribute('data-in', inCupData.bustRange);
                    cell.setAttribute('data-cm', cmCupData.bustRange);
                    cell.textContent = this.currentUnit === 'cm' ? cmCupData.bustRange : inCupData.bustRange;
                } else {
                    cell.textContent = '—';
                    cell.style.color = '#CCC';
                }
                row.appendChild(cell);
            });

            tbody.appendChild(row);
        });
        table.appendChild(tbody);
        wrapper.appendChild(table);
    }

    buildBustLookupTable() {
        const wrapper = this.querySelector('#sg-full-bust-lookup-wrapper');
        if (!wrapper || wrapper.querySelector('table')) return; // Already built

        const fullData = this.sizeData.braFull;
        if (!fullData) {
            wrapper.innerHTML = '<p style="padding:16px;">Chart data not available.</p>';
            return;
        }

        const bands = ['32', '34', '36', '38', '40'];
        const inBustRows = fullData.inches.bustToSize;
        const cmBustRows = fullData.cm.bustToSize;

        const table = document.createElement('table');
        table.className = 'sg-table';
        table.setAttribute('data-table-container', 'full-bust-lookup');

        // Header
        const thead = document.createElement('thead');
        const headerRow = document.createElement('tr');
        const bustTh = document.createElement('th');
        bustTh.textContent = 'Bust';
        headerRow.appendChild(bustTh);
        bands.forEach(band => {
            const th = document.createElement('th');
            th.textContent = 'Band ' + band;
            headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        table.appendChild(thead);

        // Use inches bustToSize for row structure (it has simple bust integers)
        const tbody = document.createElement('tbody');
        inBustRows.forEach((inRow, idx) => {
            const row = document.createElement('tr');
            const cmRow = cmBustRows[idx];

            // Bust cell
            const bustCell = document.createElement('td');
            bustCell.style.fontWeight = '600';
            const inLabel = inRow.bust ? `${inRow.bust}"` : (inRow.bustRange || '');
            const cmLabel = cmRow ? (cmRow.bustRange || '') : '';
            bustCell.setAttribute('data-in', inLabel);
            bustCell.setAttribute('data-cm', cmLabel);
            bustCell.textContent = this.currentUnit === 'cm' ? cmLabel : inLabel;
            row.appendChild(bustCell);

            // Band cells
            bands.forEach(band => {
                const cell = document.createElement('td');
                const sizeIn = inRow.sizes ? inRow.sizes[band] : null;
                cell.textContent = sizeIn || '—';
                if (!sizeIn) cell.style.color = '#CCC';
                row.appendChild(cell);
            });

            tbody.appendChild(row);
        });
        table.appendChild(tbody);
        wrapper.appendChild(table);
    }

    // ===== MODAL CONTROLS =====
    /**
     * Open the size guide modal.
     * @param {string} [tags] - Comma-separated product tags (lowercase)
     * @param {string} [handle] - Product handle (for special product matching)
     */
    open(tags, handle) {
        const overlay = document.getElementById('sizeGuideOverlay');
        if (overlay) overlay.classList.add('active');
        this.classList.add('active');
        document.body.style.overflow = 'hidden';

        // Auto-select tab based on product tags and handle
        if (tags) {
            const tab = this.detectProductTab(tags, handle);
            if (tab) {
                this.switchProductTab(tab);
            }
        }
    }

    /**
     * Detect which product tab to show based on tags and handle.
     * Priority:
     * 1. Specific product handles → bralettes (Bralettes & Bikini Tops)
     * 2. bikini_top tag → bralettes
     * 3. bra tag (excluding special handles above) → bras
     * 4. panty / bikini_bottom / garterbelt → panties (Bottoms & Garter Belts)
     * 5. bodysuit → bodysuits (Bodysuits & Swimsuits)
     * 6. leggarter → leggarters
     */
    detectProductTab(tags, handle) {
        const tagList = String(tags).toLowerCase().split(',').map(t => t.trim());
        const h = String(handle || '').toLowerCase();

        // bra tag (not special handles, not bikini_top) → Bras
        // swim_bra tag → Bras
        if (tagList.includes('bra') || tagList.includes('swim_bra')) {
            return 'bras';
        }

        // Special product handles → Bralettes & Bikini Tops
        const braletteHandles = ['chamomile-bralette', 'chamomile-short-sleeve', 'chamomile-long-sleeve-top', 'goddess-chain-bra'];
        if (braletteHandles.some(bh => h.includes(bh))) {
            return 'bralettes';
        }

        // bikini_top tag → Bralettes & Bikini Tops
        // swim_set tag → Bralettes & Bikini Tops
        if (tagList.includes('bikini_top') || tagList.includes('swim_set')) {
            return 'bralettes';
        }

        // panty / bikini_bottom / garterbelt → Bottoms & Garter Belts
        if (tagList.includes('panty') || tagList.includes('bikini_bottom') || tagList.includes('garterbelt')) {
            return 'panties';
        }

        // bodysuit → Bodysuits & Swimsuits
        if (tagList.includes('bodysuit')) {
            return 'bodysuits';
        }

        // leggarter → Leg Garters
        if (tagList.includes('leggarter')) {
            return 'leggarters';
        }

        return null;
    }

    close() {
        const overlay = document.getElementById('sizeGuideOverlay');
        if (overlay) overlay.classList.remove('active');
        this.classList.remove('active');
        document.body.style.overflow = '';
    }
}

customElements.define('size-guide-modal', SizeGuideModal);
}

(function applySizeFromGuide() {
    function parseBraSize(sizeStr) {
        const m = String(sizeStr).trim().match(/^(\d+)(.*)$/);
        if (!m) return { band: null, cup: null };
        const band = m[1];
        const cupPart = m[2].trim().split(/\s*\/\s*/)[0] || m[2].trim();
        return { band, cup: cupPart || null };
    }

    function tagsInclude(tagsStr, needles) {
        if (!tagsStr) return false;
        const lower = String(tagsStr).toLowerCase();
        return needles.some(n => lower.includes(n.toLowerCase()));
    }

    function applyToSet(productType, size) {
        const setsEl = document.querySelector('variant-picker-sets');
        if (!setsEl) return;

        const items = setsEl.querySelectorAll('.variant-picker-sets__item');
        const isBra = productType === 'bras';
        const braSizes = isBra ? parseBraSize(size) : null;

        items.forEach(item => {
            const tags = item.dataset.productTags || '';
            const isBraProduct = tagsInclude(tags, ['bra']);
            const isPantyProduct = tagsInclude(tags, ['panty', 'panties']);

            if (isBra && isBraProduct && braSizes.band && braSizes.cup) {
                applyBraSizeToItem(setsEl, item, braSizes.band, braSizes.cup);
            } else if ((productType === 'panties' && isPantyProduct) || (productType === 'bodysuits' && !isBraProduct && !isPantyProduct)) {
                applySingleSizeToItem(setsEl, item, size);
            }
        });
    }

    function applyBraSizeToItem(setsEl, item, band, cup) {
        if (!setsEl.selectVariantOption || !setsEl.openOptions || !setsEl.isOptionsOpen) return;
        if (!setsEl.isOptionsOpen(item)) {
            setsEl.openOptions(item);
        }
        const fieldsets = Array.from(item.querySelectorAll('fieldset[data-option-position]')).sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
        const legends = fieldsets.map(f => (f.querySelector('legend') || {}).textContent || '');
        const bandIdx = legends.findIndex(l => /band/i.test(l));
        const cupIdx = legends.findIndex(l => /cup/i.test(l));
        const bandField = bandIdx >= 0 ? fieldsets[bandIdx] : fieldsets[0];
        const cupField = cupIdx >= 0 ? fieldsets[cupIdx] : fieldsets[1];
        const findBtn = (field, val) => field && Array.from(field.querySelectorAll('.variant-picker-sets__value')).find(b => (b.dataset.value || b.textContent.trim()) === val);
        const bandBtn = findBtn(bandField, band);
        const cupBtn = findBtn(cupField, cup);
        if (bandBtn && !bandBtn.disabled) {
            setsEl.selectVariantOption(item, bandBtn);
        }
        if (cupBtn && !cupBtn.disabled) {
            setsEl.selectVariantOption(item, cupBtn);
        }
    }

    function applySingleSizeToItem(setsEl, item, size) {
        if (!setsEl.openOptions || !setsEl.selectVariantOption) return;
        const optionsEl = item.querySelector('.variant-picker-sets__options');
        if (!optionsEl) return;
        if (!setsEl.isOptionsOpen(item)) {
            setsEl.openOptions(item);
        }
        const sizeNorm = String(size).trim();
        const fieldsets = item.querySelectorAll('fieldset[data-option-position]');
        for (const fieldset of fieldsets) {
            const btn = Array.from(fieldset.querySelectorAll('.variant-picker-sets__value')).find(b => {
                const v = (b.dataset.value || b.textContent.trim()).toLowerCase();
                return v === sizeNorm.toLowerCase() || v === sizeNorm;
            });
            if (btn && !btn.disabled) {
                setsEl.selectVariantOption(item, btn);
                break;
            }
        }
    }

    function applyToSingleProduct(size) {
        const picker = document.querySelector('variant-picker');
        if (!picker) return;
        const formId = picker.getAttribute('form-id');
        if (!formId) return;
        const sizeNorm = String(size).trim().toLowerCase();
        const formEl = document.getElementById(formId);
        if (!formEl) return;
        const labels = picker.querySelectorAll('.variant-picker__option-values label[for], .block-swatch[for]');
        for (const label of labels) {
            const text = (label.textContent || '').trim().toLowerCase();
            if (text !== sizeNorm) continue;
            const id = label.getAttribute('for');
            const input = id ? document.getElementById(id) : null;
            if (input && input.getAttribute('form') === formId && input.matches('input[data-option-position]')) {
                input.checked = true;
                input.dispatchEvent(new Event('change', { bubbles: true }));
                break;
            }
        }
    }

    window.addEventListener('size-guide-apply-size', function(e) {
        const { productType, size, isSet } = e.detail || {};
        if (!size) return;
        if (isSet) {
            applyToSet(productType, size);
        } else {
            applyToSingleProduct(size);
        }
    });
})();
